import { useState, useEffect } from 'react'
import { useParams, useSearchParams, Link } from 'react-router-dom'
import {
  CheckCircle, XCircle, AlertTriangle, Shield, Clock, MapPin,
  Camera, Hash, User, Search, ExternalLink, Smartphone
} from 'lucide-react'

const API_BASE = import.meta.env.VITE_API_URL || 'https://api.onrecord.live'

interface VerificationData {
  code: string
  status: string
  authenticity: {
    score: number | null
    level: string
    color: string
    isScreenContent: boolean
    isPrintedMedia: boolean
    isAIGenerated: boolean
    detectionSource: string | null
  }
  capture: {
    timestamp: string | null
    location: string | null
    mediaType: string | null
    sourceType: string | null
  }
  hashChain: {
    originalHash: string | null
    watermarkedHash: string | null
    verificationCode: string
    integrityStatus: string
  }
  author: {
    username: string
    displayName: string | null
    verified: boolean
  } | null
  media: {
    thumbnailUrl: string | null
    hasMedia: boolean
    postId: string
  } | null
  createdAt: string | null
}

function getScoreConfig(score: number | null) {
  if (score === null) return { label: 'UNVERIFIED', bg: 'bg-gray-100', text: 'text-gray-600', accent: '#9CA3AF' }
  if (score >= 95) return { label: 'VERIFIED', bg: 'bg-green-50', text: 'text-green-800', accent: '#00C49A' }
  if (score >= 70) return { label: 'LIKELY AUTHENTIC', bg: 'bg-emerald-50', text: 'text-emerald-700', accent: '#10B981' }
  if (score >= 40) return { label: 'UNCERTAIN', bg: 'bg-amber-50', text: 'text-amber-700', accent: '#FCA903' }
  return { label: 'SUSPICIOUS', bg: 'bg-red-50', text: 'text-red-700', accent: '#E63946' }
}

function formatDate(iso: string | null) {
  if (!iso) return 'Unknown'
  return new Date(iso).toLocaleDateString('en-IN', {
    day: 'numeric', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: true
  })
}

function sourceLabel(type: string | null) {
  const labels: Record<string, string> = {
    'onrecord_camera': 'OnRecord Camera',
    'in-app': 'OnRecord Camera',
    'gallery': 'Gallery Upload',
    'shared_media': 'Shared Media',
    'web': 'Web Upload',
  }
  return type ? labels[type] || type : null
}

export default function Verify() {
  const { code: urlCode } = useParams()
  const [searchParams] = useSearchParams()
  const [inputCode, setInputCode] = useState(urlCode || searchParams.get('code') || '')
  const [verification, setVerification] = useState<VerificationData | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [searched, setSearched] = useState(false)

  useEffect(() => {
    if (urlCode) {
      setInputCode(urlCode)
      verify(urlCode)
    }
  }, [urlCode])

  async function verify(code: string) {
    const cleaned = code.trim().toUpperCase()
    if (!cleaned || cleaned.length < 6) {
      setError('Please enter a valid OnRecord code (at least 6 characters)')
      return
    }
    setLoading(true)
    setError(null)
    setVerification(null)
    setSearched(true)
    try {
      const res = await fetch(`${API_BASE}/verify/${encodeURIComponent(cleaned)}`)
      const data = await res.json()
      if (!res.ok || !data.success) {
        setError(data.error || 'Verification code not found')
        return
      }
      setVerification(data.verification)
      window.history.replaceState(null, '', `/verify/${cleaned}`)
    } catch {
      setError('Unable to connect to verification service. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const score = verification?.authenticity?.score
  const config = getScoreConfig(score)

  return (
    <div className="min-h-screen bg-bg-primary">
      {/* Header */}
      <header className="border-b border-border bg-white sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/login" className="flex items-center gap-2 no-underline">
            <img src="/logo.svg" alt="on record" className="w-8 h-8" />
            <span className="font-bold text-lg text-text-primary tracking-tight">on record</span>
          </Link>
          <span className="text-xs text-text-quaternary uppercase tracking-widest font-semibold">Verify</span>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-10">
        {/* Search Section */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-text-primary mb-2">Content Verification</h1>
          <p className="text-text-tertiary text-sm">
            Enter the code from any OnRecord watermarked media to verify its authenticity and origin.
          </p>
        </div>

        <form onSubmit={(e) => { e.preventDefault(); verify(inputCode) }} className="flex gap-2 mb-8">
          <div className="flex-1 relative">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-text-quaternary" />
            <input
              type="text"
              value={inputCode}
              onChange={(e) => setInputCode(e.target.value.toUpperCase())}
              placeholder="Enter OnRecord code e.g. P4X2KQRN8T1A"
              className="w-full pl-10 pr-4 py-3 bg-white border border-border rounded-xl text-text-primary placeholder-text-quaternary focus:outline-none focus:border-onrecord-orange focus:ring-2 focus:ring-onrecord-orange-bg font-mono tracking-wider text-sm"
              maxLength={16}
              autoFocus
            />
          </div>
          <button
            type="submit"
            disabled={loading || !inputCode.trim()}
            className="px-6 py-3 bg-onrecord-orange hover:bg-onrecord-orange-dark disabled:bg-border disabled:text-text-quaternary text-white font-semibold rounded-xl transition-colors text-sm"
          >
            {loading ? 'Verifying...' : 'Verify'}
          </button>
        </form>

        {/* Error */}
        {error && searched && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6 text-center">
            <XCircle className="w-8 h-8 text-error mx-auto mb-2" />
            <p className="text-error text-sm">{error}</p>
          </div>
        )}

        {/* Results */}
        {verification && (
          <div className="space-y-4">
            {/* Score Banner */}
            <div className={`${config.bg} border rounded-xl p-6 text-center`} style={{ borderColor: config.accent }}>
              <div className="flex items-center justify-center gap-2 mb-2">
                {score !== null && score >= 70 ? (
                  <CheckCircle className="w-8 h-8" style={{ color: config.accent }} />
                ) : score !== null && score < 40 ? (
                  <XCircle className="w-8 h-8" style={{ color: config.accent }} />
                ) : (
                  <AlertTriangle className="w-8 h-8" style={{ color: config.accent }} />
                )}
                <span className={`text-4xl font-bold ${config.text}`}>
                  {score !== null ? `${score}%` : 'N/A'}
                </span>
              </div>
              <div className={`text-sm font-semibold ${config.text} uppercase tracking-widest`}>
                {config.label}
              </div>
              {score !== null && (
                <div className="mt-3 w-full bg-black/5 rounded-full h-2 max-w-xs mx-auto">
                  <div
                    className="h-2 rounded-full transition-all duration-500"
                    style={{ width: `${score}%`, backgroundColor: config.accent }}
                  />
                </div>
              )}
            </div>

            {/* Capture Details */}
            <div className="bg-white border border-border rounded-xl p-5">
              <h3 className="text-xs font-semibold text-text-quaternary uppercase tracking-wider mb-4">Capture Details</h3>
              <div className="space-y-3">
                <DetailRow icon={<Clock className="w-4 h-4" />} label="Timestamp" value={formatDate(verification.capture.timestamp)} />
                <DetailRow icon={<MapPin className="w-4 h-4" />} label="Location" value={verification.capture.location || 'Not available'} />
                <DetailRow icon={<Camera className="w-4 h-4" />} label="Media Type" value={verification.capture.mediaType ? verification.capture.mediaType.charAt(0).toUpperCase() + verification.capture.mediaType.slice(1) : 'Unknown'} />
                {verification.capture.sourceType && (
                  <DetailRow icon={<Smartphone className="w-4 h-4" />} label="Source" value={sourceLabel(verification.capture.sourceType) || verification.capture.sourceType} />
                )}
                {verification.author && (
                  <DetailRow icon={<User className="w-4 h-4" />} label="Author" value={`@${verification.author.username}${verification.author.verified ? ' (Verified)' : ''}`} />
                )}
              </div>
            </div>

            {/* AI Analysis */}
            <div className="bg-white border border-border rounded-xl p-5">
              <h3 className="text-xs font-semibold text-text-quaternary uppercase tracking-wider mb-4">AI Analysis</h3>
              <div className="space-y-2.5">
                <CheckRow passed={!verification.authenticity.isScreenContent} label="No screen capture detected" />
                <CheckRow passed={!verification.authenticity.isPrintedMedia} label="No print/scan detected" />
                <CheckRow passed={!verification.authenticity.isAIGenerated} label="No AI generation detected" />
                <CheckRow
                  passed={verification.authenticity.detectionSource === 'server'}
                  label={verification.authenticity.detectionSource === 'server' ? 'Server-verified' : 'Detection pending'}
                  neutral={verification.authenticity.detectionSource !== 'server'}
                />
              </div>
            </div>

            {/* Hash Chain */}
            <div className="bg-white border border-border rounded-xl p-5">
              <h3 className="text-xs font-semibold text-text-quaternary uppercase tracking-wider mb-4">Hash Chain Integrity</h3>
              <div className="space-y-3">
                <DetailRow
                  icon={verification.hashChain.integrityStatus === 'intact'
                    ? <CheckCircle className="w-4 h-4 text-success" />
                    : <AlertTriangle className="w-4 h-4 text-warning" />}
                  label="Status"
                  value={verification.hashChain.integrityStatus === 'intact' ? 'Intact — not tampered' : 'Unavailable'}
                />
                {verification.hashChain.originalHash && (
                  <DetailRow icon={<Hash className="w-4 h-4" />} label="Original" value={verification.hashChain.originalHash} mono />
                )}
                {verification.hashChain.watermarkedHash && (
                  <DetailRow icon={<Hash className="w-4 h-4" />} label="Processed" value={verification.hashChain.watermarkedHash} mono />
                )}
                <DetailRow icon={<Hash className="w-4 h-4" />} label="Code" value={verification.hashChain.verificationCode} mono />
              </div>
            </div>

            {/* View Post Link */}
            {verification.media?.postId && (
              <Link
                to={`/p/${verification.media.postId}`}
                className="block bg-white border border-border rounded-xl p-4 text-center hover:border-onrecord-orange transition-colors no-underline"
              >
                <span className="text-onrecord-orange text-sm font-semibold flex items-center justify-center gap-2">
                  View Original Post <ExternalLink className="w-4 h-4" />
                </span>
              </Link>
            )}

            {/* Explainer */}
            <div className="bg-bg-secondary border border-border-light rounded-xl p-5 text-center">
              <h3 className="text-sm font-semibold text-text-secondary mb-2">How does verification work?</h3>
              <p className="text-xs text-text-tertiary leading-relaxed max-w-md mx-auto">
                OnRecord captures GPS location, timestamp, and device data at the exact moment of recording,
                then seals it with a cryptographic hash chain. AI analysis checks for screen captures, print scans,
                deepfakes, and digital manipulation. The result is tamper-proof evidence of what happened, when, and where.
              </p>
            </div>

            {/* App Download */}
            <div className="flex items-center justify-center gap-4 pt-2 pb-4">
              <a href="https://apps.apple.com/app/on-record/id1534718618" target="_blank" rel="noopener noreferrer"
                className="px-4 py-2 bg-bg-tertiary hover:bg-border rounded-lg text-xs text-text-tertiary transition-colors no-underline">
                App Store
              </a>
              <a href="https://play.google.com/store/apps/details?id=com.onrecord.live" target="_blank" rel="noopener noreferrer"
                className="px-4 py-2 bg-bg-tertiary hover:bg-border rounded-lg text-xs text-text-tertiary transition-colors no-underline">
                Google Play
              </a>
            </div>
          </div>
        )}

        {/* Empty state */}
        {!searched && !loading && (
          <div className="text-center py-16">
            <Shield className="w-16 h-16 text-border mx-auto mb-4" />
            <p className="text-text-quaternary text-sm">
              Look for the code in the watermark on any OnRecord media.
            </p>
            <p className="text-text-tertiary text-sm mt-1">
              Format: <span className="font-mono text-text-secondary">on record &middot; CODE</span>
            </p>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="border-t border-border-light py-6">
        <div className="flex flex-wrap justify-center gap-x-3 gap-y-2 text-xs text-text-quaternary">
          <a href="/about" className="hover:underline">About</a>
          <a href="/terms" className="hover:underline">Terms</a>
          <a href="/privacy" className="hover:underline">Privacy</a>
        </div>
        <div className="mt-2 text-center text-xs text-text-quaternary">&copy; 2026 on record</div>
      </footer>
    </div>
  )
}

function DetailRow({ icon, label, value, mono }: { icon: React.ReactNode, label: string, value: string, mono?: boolean }) {
  return (
    <div className="flex items-start gap-3">
      <span className="text-text-quaternary mt-0.5">{icon}</span>
      <div className="flex-1 min-w-0">
        <div className="text-xs text-text-quaternary">{label}</div>
        <div className={`text-sm text-text-primary ${mono ? 'font-mono text-xs break-all' : ''}`}>{value}</div>
      </div>
    </div>
  )
}

function CheckRow({ passed, label, neutral }: { passed: boolean, label: string, neutral?: boolean }) {
  return (
    <div className="flex items-center gap-2.5">
      {neutral ? (
        <AlertTriangle className="w-4 h-4 text-text-quaternary" />
      ) : passed ? (
        <CheckCircle className="w-4 h-4 text-success" />
      ) : (
        <XCircle className="w-4 h-4 text-error" />
      )}
      <span className={`text-sm ${neutral ? 'text-text-quaternary' : passed ? 'text-text-secondary' : 'text-error'}`}>{label}</span>
    </div>
  )
}
